import { MEMBERS, RECORDS, type DataRecord, type Member } from '@/data/records';

/** 멤버 활동 상태 — 연결된 항목에서 파생한다(멤버 자체에 저장하지 않아 어긋나지 않는다). */
export const ACTIVITY_STATUSES = ['활동 중', '활동 예정', '비활동'] as const;
export type Activity = (typeof ACTIVITY_STATUSES)[number];

export const ACTIVITY_TONE: Record<Activity, 'success' | 'info' | 'neutral'> = {
  '활동 중': 'success',
  '활동 예정': 'info',
  비활동: 'neutral',
};

export interface MemberRow {
  member: Member;
  /** 시작일 최신순 */
  records: DataRecord[];
  activity: Activity;
  /** 지금(또는 곧) 연결된 항목의 리소스 */
  currentResourceId: string | null;
}

const ACTIVE: DataRecord['status'][] = ['진행 중', '종료 임박'];
const UPCOMING: DataRecord['status'][] = ['검토 대기', '예정'];

export function activityOf(records: DataRecord[]): Activity {
  if (records.some((r) => ACTIVE.includes(r.status))) {
    return '활동 중';
  }
  if (records.some((r) => UPCOMING.includes(r.status))) {
    return '활동 예정';
  }
  return '비활동';
}

export function buildMemberRow(member: Member, allRecords: DataRecord[] = RECORDS): MemberRow {
  const records = allRecords
    .filter((r) => r.memberIds.includes(member.id))
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
  const current =
    records.find((r) => ACTIVE.includes(r.status)) ??
    records.find((r) => UPCOMING.includes(r.status));
  return {
    member,
    records,
    activity: activityOf(records),
    currentResourceId: current?.resourceId ?? null,
  };
}

export const MEMBER_ROWS: MemberRow[] = MEMBERS.map((member) => buildMemberRow(member));

export interface MemberQuery {
  q: string;
  activity: string[];
}

/** 검색(이름·이메일·연락처·ID) + 활동 상태 필터. 상태끼리는 OR. */
export function filterMembers(rows: MemberRow[], query: MemberQuery) {
  const keyword = query.q.trim().toLowerCase();
  return rows.filter((row) => {
    const { member } = row;
    if (
      keyword &&
      ![member.name, member.email, member.phone, member.id]
        .join(' ')
        .toLowerCase()
        .includes(keyword)
    ) {
      return false;
    }
    return query.activity.length === 0 || query.activity.includes(row.activity);
  });
}

const SORTERS: Record<string, ((row: MemberRow) => string | number) | undefined> = {
  name: (row) => row.member.name,
  activity: (row) => ACTIVITY_STATUSES.indexOf(row.activity),
};

/** DataTable 정렬값(`${key},${asc|desc}`). 모르는 키·null이면 원래 순서. */
export function sortMembers(rows: MemberRow[], sort: string | null) {
  const [key, dir] = (sort ?? '').split(',');
  const accessor = key ? SORTERS[key] : undefined;
  if (!accessor) {
    return rows;
  }
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = accessor(a);
    const right = accessor(b);
    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * sign;
    }
    return String(left).localeCompare(String(right), 'ko') * sign;
  });
}
